import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { supabase } from '@/integrations/supabase/client.safe'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Textarea } from '@/components/ui/textarea'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { 
  ArrowLeft, 
  Download, 
  Save, 
  FileText, 
  Clock, 
  CheckCircle,
  AlertCircle,
  Users,
  Calendar,
  Brain,
  Wand2
} from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import jsPDF from 'jspdf'
import html2canvas from 'html2canvas'
import { formatWithUTC, formatDateOnly } from '@/lib/datetime'

interface FinalReport {
  id: string
  audit_id: string
  version_number: number
  title: string
  content: {
    executive_summary: string
    module_reports: Record<string, string>
    findings_summary: {
      critical: number
      high: number
      medium: number
      low: number
    }
    recommendations: string
    conclusion: string
  }
  excluded_modules: string[]
  created_by: string
  created_at: string
  updated_at: string
  is_current_version: boolean
}

interface ModuleSignoff {
  id: string
  module_id: string
  signed_off_by: string
  signed_off_at: string
  comment?: string
}

interface AuditSignoff {
  id: string
  audit_id: string
  signed_off_by: string
  signed_off_at: string
  comment?: string
}

interface Organization {
  id: string
  name: string
}

interface UserProfile {
  id: string
  full_name?: string
}

interface FinalReportPageProps {
  organization: Organization | null
  profile: UserProfile | null
}

const getModulesFromAuditData = (auditData: any) => {
  if (!auditData?.data_input?.excludedModules) return []
  
  const allModules = [
    { id: 'revenue_controls', name: 'Intäktskontroller' },
    { id: 'cost_controls', name: 'Kostnadskontroller' },
    { id: 'payroll_personnel', name: 'Lön & Personal' },
    { id: 'taxes_fees', name: 'Skatter & avgifter' },
    { id: 'balance_controls', name: 'Balansräkning' },
    { id: 'other_documents', name: 'Övriga dokument' }
  ]
  
  return allModules.filter(module => !auditData.data_input.excludedModules.includes(module.id))
}

export function FinalReportPage({ organization, profile }: FinalReportPageProps) {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { toast } = useToast()
  
  const [audit, setAudit] = useState<any>(null)
  const [finalReport, setFinalReport] = useState<FinalReport | null>(null)
  const [reportVersions, setReportVersions] = useState<FinalReport[]>([])
  const [moduleSignoffs, setModuleSignoffs] = useState<ModuleSignoff[]>([])
  const [auditSignoff, setAuditSignoff] = useState<AuditSignoff | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [generatingAI, setGeneratingAI] = useState(false)
  
  // Form states
  const [reportTitle, setReportTitle] = useState('')
  const [executiveSummary, setExecutiveSummary] = useState('')
  const [moduleReports, setModuleReports] = useState<Record<string, string>>({})
  const [recommendations, setRecommendations] = useState('')
  const [conclusion, setConclusion] = useState('')
  const [excludedModules, setExcludedModules] = useState<string[]>([])

  useEffect(() => {
    if (id) {
      fetchReportData()
    }
  }, [id])

  const fetchReportData = async () => {
    try {
      setLoading(true)
      
      // Fetch audit data
      const { data: auditData, error: auditError } = await supabase
        .from('audits')
        .select('*')
        .eq('id', id)
        .single()

      if (auditError) throw auditError
      setAudit(auditData)

      // Fetch current final report
      const { data: reportData, error: reportError } = await supabase
        .from('final_reports')
        .select('*')
        .eq('audit_id', id)
        .eq('is_current_version', true)
        .single()

      if (reportData) {
        setFinalReport(reportData as FinalReport)
        setReportTitle(reportData.title)
        const content = reportData.content as any
        setExecutiveSummary(content?.executive_summary || '')
        setModuleReports(content?.module_reports || {})
        setRecommendations(content?.recommendations || '')
        setConclusion(content?.conclusion || '')
        setExcludedModules(reportData.excluded_modules || [])
      } else {
        // Initialize with default values
        setReportTitle(`Slutrapport - ${auditData.title}`)
        setExcludedModules(auditData.data_input?.excludedModules || [])
      }

      // Fetch all report versions
      const { data: versionsData, error: versionsError } = await supabase
        .from('final_reports')
        .select('*')
        .eq('audit_id', id)
        .order('version_number', { ascending: false })

      if (versionsError) throw versionsError
      setReportVersions((versionsData || []) as FinalReport[])

      // Fetch signoffs
      const { data: moduleSignoffData, error: moduleSignoffError } = await supabase
        .from('module_signoffs')
        .select('*')
        .eq('audit_id', id)

      if (moduleSignoffError) throw moduleSignoffError
      setModuleSignoffs(moduleSignoffData || [])

      const { data: auditSignoffData, error: auditSignoffError } = await supabase
        .from('audit_signoffs')
        .select('*')
        .eq('audit_id', id)
        .single()

      if (auditSignoffData) {
        setAuditSignoff(auditSignoffData)
      }

    } catch (error) {
      console.error('Error fetching report data:', error)
      toast({
        title: "Fel",
        description: "Kunde inte hämta rapportdata",
        variant: "destructive"
      })
    } finally {
      setLoading(false)
    }
  }

  const generateWithAI = async () => {
    if (!audit || !profile) return

    try {
      setGeneratingAI(true)
      
      toast({
        title: "Genererar rapport",
        description: "AuditAI skapar rapportinnehåll baserat på ärendedata..."
      })

      // Call the edge function for report generation
      const { data, error } = await supabase.functions.invoke('generate-audit-report', {
        body: { auditId: id }
      })
      
      if (error) {
        throw new Error(error.message || 'Kunde inte generera rapport')
      }
      
      setExecutiveSummary(data.executiveSummary)
      setModuleReports(data.moduleReports)
      setRecommendations(data.recommendations)
      setConclusion(data.conclusion)
      
      toast({
        title: "Rapport genererad",
        description: "AuditAI har skapat rapportinnehåll. Granska och redigera vid behov."
      })

    } catch (error) {
      console.error('Error generating AI report:', error)
      toast({
        title: "Fel",
        description: error instanceof Error ? error.message : "Kunde inte generera rapport med AuditAI",
        variant: "destructive"
      })
    } finally {
      setGeneratingAI(false)
    }
  }

  const saveReport = async () => {
    if (!audit || !profile) return

    try {
      setSaving(true)

      const reportContent = {
        executive_summary: executiveSummary,
        module_reports: moduleReports,
        findings_summary: {
          critical: 2,
          high: 3,
          medium: 5,
          low: 1
        },
        recommendations: recommendations,
        conclusion: conclusion
      }

      const { data, error } = await supabase
        .from('final_reports')
        .insert({
          audit_id: id!,
          title: reportTitle,
          content: reportContent,
          excluded_modules: excludedModules,
          created_by: profile.id
        })
        .select()
        .single()

      if (error) throw error

      setFinalReport(data as FinalReport)
      
      toast({
        title: "Rapport sparad",
        description: `Version ${data.version_number} har skapats`
      })

      // Refresh versions
      await fetchReportData()

    } catch (error) {
      console.error('Error saving report:', error)
      toast({
        title: "Fel",
        description: "Kunde inte spara rapporten",
        variant: "destructive"
      })
    } finally {
      setSaving(false)
    }
  }

  const generatePDF = async () => {
    if (!finalReport || !audit) return

    try {
      setGenerating(true)
      
      // Create a temporary div with the report content
      const reportElement = document.createElement('div')
      reportElement.style.padding = '40px'
      reportElement.style.backgroundColor = 'white'
      reportElement.style.fontFamily = 'Arial, sans-serif'
      reportElement.style.lineHeight = '1.6'
      reportElement.style.color = '#000'
      
      // Generate report HTML
      reportElement.innerHTML = `
        <div style="text-align: center; margin-bottom: 40px;">
          <h1 style="color: #1a365d; margin-bottom: 10px;">${finalReport.title}</h1>
          <h2 style="color: #4a5568; font-weight: normal;">${organization?.name || '[Organisationsnamn]'}</h2>
          <p style="color: #718096; margin-top: 20px;">
            Rapport genererad: ${formatDateOnly(new Date())} • 
            Version: ${finalReport.version_number}
          </p>
        </div>

        <div style="margin-bottom: 30px;">
          <h2 style="color: #1a365d; border-bottom: 2px solid #e2e8f0; padding-bottom: 10px;">Sammanfattning</h2>
          <p style="margin-top: 15px; white-space: pre-wrap;">${finalReport.content.executive_summary || 'Ingen sammanfattning tillgänglig.'}</p>
        </div>

        <div style="margin-bottom: 30px;">
          <h2 style="color: #1a365d; border-bottom: 2px solid #e2e8f0; padding-bottom: 10px;">Findings per allvarlighetsgrad</h2>
          <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 15px; margin-top: 15px;">
            <div style="padding: 15px; background: #fed7d7; border-radius: 8px;">
              <strong>Kritisk:</strong> ${finalReport.content.findings_summary?.critical || 0}
            </div>
            <div style="padding: 15px; background: #feebc8; border-radius: 8px;">
              <strong>Hög:</strong> ${finalReport.content.findings_summary?.high || 0}
            </div>
            <div style="padding: 15px; background: #fefcbf; border-radius: 8px;">
              <strong>Medium:</strong> ${finalReport.content.findings_summary?.medium || 0}
            </div>
            <div style="padding: 15px; background: #c6f6d5; border-radius: 8px;">
              <strong>Låg:</strong> ${finalReport.content.findings_summary?.low || 0}
            </div>
          </div>
        </div>

        ${getModulesFromAuditData(audit).map(module => {
          return `
            <div style="margin-bottom: 30px; page-break-inside: avoid;">
              <h2 style="color: #1a365d; border-bottom: 2px solid #e2e8f0; padding-bottom: 10px;">${module.name}</h2>
              <p style="margin-top: 15px; white-space: pre-wrap;">${finalReport.content.module_reports?.[module.id] || `Rapport för ${module.name} kommer att kompletteras.`}</p>
            </div>
          `
        }).join('')}

        <div style="margin-bottom: 30px;">
          <h2 style="color: #1a365d; border-bottom: 2px solid #e2e8f0; padding-bottom: 10px;">Ej genomförda moduler</h2>
          ${finalReport.excluded_modules.length > 0 ? 
            `<ul style="margin-top: 15px;">
              ${finalReport.excluded_modules.map(moduleId => {
                const allModules = [
                  { id: 'revenue_controls', name: 'Intäktskontroller' },
                  { id: 'cost_controls', name: 'Kostnadskontroller' },
                  { id: 'payroll_personnel', name: 'Lön & Personal' },
                  { id: 'taxes_fees', name: 'Skatter & avgifter' },
                  { id: 'balance_controls', name: 'Balansräkning' },
                  { id: 'other_documents', name: 'Övriga dokument' }
                ]
                const module = allModules.find(m => m.id === moduleId)
                return `<li style="margin-bottom: 5px;">${module?.name || moduleId}</li>`
              }).join('')}
            </ul>` : 
            '<p style="margin-top: 15px;">Alla moduler har genomförts.</p>'
          }
        </div>

        <div style="margin-bottom: 30px;">
          <h2 style="color: #1a365d; border-bottom: 2px solid #e2e8f0; padding-bottom: 10px;">Rekommendationer</h2>
          <p style="margin-top: 15px; white-space: pre-wrap;">${finalReport.content.recommendations || 'Inga specifika rekommendationer.'}</p>
        </div>

        <div style="margin-bottom: 30px;">
          <h2 style="color: #1a365d; border-bottom: 2px solid #e2e8f0; padding-bottom: 10px;">Slutsats</h2>
          <p style="margin-top: 15px; white-space: pre-wrap;">${finalReport.content.conclusion || 'Slutsats kommer att kompletteras.'}</p>
        </div>

        <div style="margin-top: 40px; padding: 20px; background: #f7fafc; border-radius: 8px;">
          <h2 style="color: #1a365d; margin-bottom: 20px;">Godkännanden</h2>
          
          <div style="margin-bottom: 20px;">
            <h3 style="color: #2d3748; margin-bottom: 10px;">Modulgodkännanden (Senior):</h3>
            ${moduleSignoffs.length > 0 ? 
              (() => {
                const uniqueSignoffs = moduleSignoffs.filter((signoff, index, arr) => 
                  arr.findIndex(s => s.module_id.replace('skatter-avgifter', 'taxes_fees') === signoff.module_id.replace('skatter-avgifter', 'taxes_fees')) === index
                )
                return uniqueSignoffs.map(signoff => {
                  const allModules = getModulesFromAuditData(audit)
                  const normalizedId = signoff.module_id.replace('skatter-avgifter', 'taxes_fees')
                  const module = allModules.find(m => m.id === normalizedId)
                  return `
                    <div style="margin-bottom: 8px; padding: 8px; background: white; border-radius: 4px;">
                      <strong>${module?.name || normalizedId}:</strong> Godkänt av ${signoff.signed_off_by} 
                      <span style="color: #718096;">(${formatWithUTC(signoff.signed_off_at)})</span>
                    </div>
                  `
                }).join('')
              })() : 
              '<p style="color: #718096;">Inga modulgodkännanden registrerade.</p>'
            }
          </div>

          <div>
            <h3 style="color: #2d3748; margin-bottom: 10px;">Slutgodkännande (Påskrivande revisor):</h3>
            ${auditSignoff ? 
              `<div style="padding: 8px; background: white; border-radius: 4px;">
                <strong>Godkänt av:</strong> ${auditSignoff.signed_off_by}
                <br>
                <strong>Datum:</strong> ${formatWithUTC(auditSignoff.signed_off_at)}
              </div>` : 
              '<p style="color: #718096;">Slutgodkännande ej registrerat.</p>'
            }
          </div>
        </div>
      `

      document.body.appendChild(reportElement)

      // Convert to canvas and then PDF
      const canvas = await html2canvas(reportElement, {
        scale: 2,
        useCORS: true,
        allowTaint: true
      })

      document.body.removeChild(reportElement)

      const imgData = canvas.toDataURL('image/png')
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      })

      const imgWidth = 210
      const pageHeight = 295
      const imgHeight = (canvas.height * imgWidth) / canvas.width
      let heightLeft = imgHeight

      let position = 0

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight)
      heightLeft -= pageHeight

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight
        pdf.addPage()
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight)
        heightLeft -= pageHeight
      }

      const fileName = `${reportTitle.replace(/[^a-zA-Z0-9åäöÅÄÖ\s]/g, '')}_v${finalReport.version_number}.pdf`
      pdf.save(fileName)

      toast({
        title: "PDF genererad",
        description: "Rapporten har exporterats som PDF"
      })

    } catch (error) {
      console.error('Error generating PDF:', error)
      toast({
        title: "Fel",
        description: "Kunde inte generera PDF",
        variant: "destructive"
      })
    } finally {
      setGenerating(false)
    }
  }

  // Removed - using formatWithUTC from @/lib/datetime

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  if (!audit) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">Ärende kunde inte hittas</p>
        <Button onClick={() => navigate('/cases')} className="mt-4">
          Tillbaka till ärenden
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button 
          variant="ghost" 
          onClick={() => navigate(`/cases/${id}`)}
          className="gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Tillbaka till ärende
        </Button>
        <div className="flex-1">
          <h1 className="text-3xl font-bold tracking-tight">Slutrapport</h1>
          <p className="text-muted-foreground mt-1">{audit.title}</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={generateWithAI}
            disabled={generatingAI}
            variant="outline"
            className="gap-2"
          >
            <Wand2 className="h-4 w-4" />
            {generatingAI ? 'Genererar...' : 'Generera med AuditAI'}
          </Button>
          <Button
            onClick={saveReport}
            disabled={saving || !reportTitle.trim()}
            className="gap-2"
          >
            <Save className="h-4 w-4" />
            {saving ? 'Sparar...' : 'Spara rapport'}
          </Button>
          {finalReport && (
            <Button
              onClick={generatePDF}
              disabled={generating}
              variant="outline"
              className="gap-2"
            >
              <Download className="h-4 w-4" />
              {generating ? 'Genererar...' : 'Exportera PDF'}
            </Button>
          )}
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-4">
        {/* Main Report Content */}
        <div className="lg:col-span-3 space-y-6">
          {/* Report Title */}
          <Card>
            <CardHeader>
              <CardTitle>Rapporttitel</CardTitle>
            </CardHeader>
            <CardContent>
              <Input
                value={reportTitle}
                onChange={(e) => setReportTitle(e.target.value)}
                placeholder="Ange rapporttitel..."
              />
            </CardContent>
          </Card>

          {/* Executive Summary */}
          <Card>
            <CardHeader>
              <CardTitle>Sammanfattning</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                value={executiveSummary}
                onChange={(e) => setExecutiveSummary(e.target.value)}
                placeholder="Skriv en sammanfattning av revisionen..."
                className="min-h-[150px]"
              />
            </CardContent>
          </Card>

          {/* Module Reports */}
          <Card>
            <CardHeader>
              <CardTitle>Modulrapporter</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {getModulesFromAuditData(audit).map((module) => {
                return (
                  <div key={module.id} className="space-y-2">
                    <Label htmlFor={`module-${module.id}`}>{module.name}</Label>
                    <Textarea
                      id={`module-${module.id}`}
                      value={moduleReports[module.id] || ''}
                      onChange={(e) => setModuleReports(prev => ({
                        ...prev,
                        [module.id]: e.target.value
                      }))}
                      placeholder={`Rapport för ${module.name}...`}
                      className="min-h-[100px]"
                    />
                  </div>
                )
              })}
            </CardContent>
          </Card>

          {/* Recommendations */}
          <Card>
            <CardHeader>
              <CardTitle>Rekommendationer</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                value={recommendations}
                onChange={(e) => setRecommendations(e.target.value)}
                placeholder="Ange rekommendationer..."
                className="min-h-[100px]"
              />
            </CardContent>
          </Card>

          {/* Conclusion */}
          <Card>
            <CardHeader>
              <CardTitle>Slutsats</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                value={conclusion}
                onChange={(e) => setConclusion(e.target.value)}
                placeholder="Skriv slutsats..."
                className="min-h-[100px]"
              />
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Report Status */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Rapportstatus</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">
                  {finalReport ? `Version ${finalReport.version_number}` : 'Ej sparad'}
                </span>
              </div>
              {finalReport && (
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">
                  {formatWithUTC(finalReport.updated_at)}
                </span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Excluded Modules */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Ej genomförda moduler</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {excludedModules.length > 0 ? (
                  excludedModules.map(moduleId => {
                    const allModules = [
                      { id: 'revenue_controls', name: 'Intäktskontroller' },
                      { id: 'cost_controls', name: 'Kostnadskontroller' },
                      { id: 'payroll_personnel', name: 'Lön & Personal' },
                      { id: 'taxes_fees', name: 'Skatter & avgifter' },
                      { id: 'balance_controls', name: 'Balansräkning' },
                      { id: 'other_documents', name: 'Övriga dokument' }
                    ]
                    const module = allModules.find(m => m.id === moduleId)
                    return (
                      <Badge key={moduleId} variant="secondary" className="mr-2">
                        {module?.name || moduleId}
                      </Badge>
                    )
                  })
                ) : (
                  <p className="text-sm text-muted-foreground">Alla moduler inkluderade</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Signoffs Status */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Godkännanden</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Senior sign-offs</span>
                </div>
                <div className="text-xs text-muted-foreground">
                  {(() => {
                    const includedModules = getModulesFromAuditData(audit)
                    const uniqueSignoffs = new Set(moduleSignoffs.map(s => s.module_id.replace('skatter-avgifter', 'taxes_fees')))
                    return `${uniqueSignoffs.size} av ${includedModules.length} moduler`
                  })()}
                </div>
              </div>
              
              <Separator />
              
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className={`h-4 w-4 ${auditSignoff ? 'text-success' : 'text-muted-foreground'}`} />
                  <span className="text-sm font-medium">Påskrivande revisor</span>
                </div>
                <div className="text-xs text-muted-foreground">
                  {auditSignoff ? 'Godkänt' : 'Ej godkänt'}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Version History */}
          {reportVersions.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Versionshistorik</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {reportVersions.slice(0, 5).map((version) => (
                    <div key={version.id} className="flex items-center justify-between text-xs">
                      <span>Version {version.version_number}</span>
                      <span className="text-muted-foreground">
                        {formatWithUTC(version.created_at)}
                      </span>
                    </div>
                  ))}
                  {reportVersions.length > 5 && (
                    <p className="text-xs text-muted-foreground">
                      +{reportVersions.length - 5} fler versioner
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  )
}